import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axiosInstance from 'axiosInstance';

const initialForms = {
  opportunity: {
    company_id: '',
    title: '',
    description: '',
    pipeline_stage_id: '',
    interest_level: 'warm',
    probability_percent: '50',
    expected_close_date: '',
    next_followup_at: ''
  },
  company: {
    name: '',
    industry: '',
    website: '',
    source_type: 'inbound',
    status: 'lead'
  },
  contact: {
    company_id: '',
    full_name: '',
    email: '',
    role: '',
    phone: ''
  },
  task: {
    company_id: '',
    opportunity_id: '',
    title: '',
    description: '',
    due_at: '',
    priority: 'medium',
    task_type: 'followup'
  }
};

const getModalTitleKey = mode => {
  switch (mode) {
    case 'company':
      return 'SALES.NEW_COMPANY';
    case 'contact':
      return 'SALES.NEW_CONTACT';
    case 'task':
      return 'SALES.NEW_TASK';
    case 'opportunity':
    default:
      return 'SALES.NEW_OPPORTUNITY';
  }
};

const buildDefaultDueDate = () => {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const buildDefaultFollowupDate = () => {
  const date = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const SalesQuickCreateModal = ({
  show,
  mode,
  onHide,
  companies,
  opportunities,
  stages,
  defaultCompanyId,
  defaultOpportunityId,
  onCreated,
  onRequestModeChange
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [error, setError] = useState('');
  const [contacts, setContacts] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState(companies || []);
  const [form, setForm] = useState(initialForms.opportunity);

  useEffect(() => {
    setAvailableCompanies(companies || []);
  }, [companies]);

  useEffect(() => {
    if (!show) return;
    if ((companies || []).length) return;

    let mounted = true;
    setLoadingCompanies(true);
    axiosInstance
      .get('sales/companies/')
      .then(response => {
        if (!mounted) return;
        setAvailableCompanies(response.data?.companies || []);
      })
      .catch(() => {
        if (!mounted) return;
        setAvailableCompanies([]);
      })
      .finally(() => {
        if (mounted) setLoadingCompanies(false);
      });

    return () => {
      mounted = false;
    };
  }, [companies, show]);

  useEffect(() => {
    if (!show) return;

    const baseForm = {
      ...initialForms[mode]
    };

    if ('company_id' in baseForm && defaultCompanyId) {
      baseForm.company_id = String(defaultCompanyId);
    }
    if (mode === 'task') {
      baseForm.due_at = buildDefaultDueDate();
      if (defaultOpportunityId) {
        baseForm.opportunity_id = String(defaultOpportunityId);
      }
    }
    if (mode === 'opportunity' && stages.length) {
      baseForm.pipeline_stage_id = String(stages[0].id);
      baseForm.probability_percent = String(stages[0].default_probability_percent || 50);
      baseForm.next_followup_at = buildDefaultFollowupDate();
    }

    setForm(baseForm);
    setContacts([]);
    setError('');
  }, [defaultCompanyId, defaultOpportunityId, mode, show, stages]);

  useEffect(() => {
    if (!show) return;
    if (!form.company_id) {
      setContacts([]);
      return;
    }
    if (mode !== 'contact' && mode !== 'task') {
      return;
    }

    let mounted = true;
    setLoadingContacts(true);
    axiosInstance
      .get(`sales/companies/${form.company_id}/contacts/`)
      .then(response => {
        if (!mounted) return;
        setContacts(response.data?.contacts || []);
      })
      .catch(() => {
        if (!mounted) return;
        setContacts([]);
      })
      .finally(() => {
        if (mounted) setLoadingContacts(false);
      });

    return () => {
      mounted = false;
    };
  }, [form.company_id, mode, show]);

  const companyOptions = useMemo(
    () => (availableCompanies || []).map(item => ({ value: String(item.id), label: item.name })),
    [availableCompanies]
  );
  const companyOpportunities = useMemo(() => {
    if (!form.company_id) return opportunities || [];
    return (opportunities || []).filter(item => String(item.company_id) === String(form.company_id));
  }, [form.company_id, opportunities]);

  const updateField = (field, value) => {
    setForm(previous => ({
      ...previous,
      [field]: value
    }));
  };

  useEffect(() => {
    if (mode !== 'opportunity') return;
    const stage = stages.find(item => String(item.id) === String(form.pipeline_stage_id));
    if (!stage) return;
    setForm(previous => ({
      ...previous,
      probability_percent: String(stage.default_probability_percent || previous.probability_percent || 50)
    }));
  }, [form.pipeline_stage_id, mode, stages]);

  useEffect(() => {
    if (mode !== 'task') return;
    if (!form.company_id) {
      setForm(previous => ({ ...previous, opportunity_id: '' }));
      return;
    }
    const hasCurrentOpportunity = companyOpportunities.some(item => String(item.id) === String(form.opportunity_id));
    if (!hasCurrentOpportunity) {
      setForm(previous => ({
        ...previous,
        opportunity_id: companyOpportunities[0] ? String(companyOpportunities[0].id) : ''
      }));
    }
  }, [companyOpportunities, form.company_id, form.opportunity_id, mode]);

  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      let response;

      if (mode === 'company') {
        response = await axiosInstance.post('sales/companies/', form);
        onCreated?.({ type: 'company', company: response.data?.company || null });
      }

      if (mode === 'contact') {
        response = await axiosInstance.post(`sales/companies/${form.company_id}/contacts/`, {
          full_name: form.full_name,
          email: form.email,
          role: form.role,
          phone: form.phone
        });
        onCreated?.({ type: 'contact', contact: response.data?.contact || null, companyId: form.company_id });
      }

      if (mode === 'opportunity') {
        response = await axiosInstance.post('sales/opportunities/', {
          ...form,
          company_id: Number(form.company_id),
          pipeline_stage_id: form.pipeline_stage_id ? Number(form.pipeline_stage_id) : undefined,
          probability_percent: form.probability_percent ? Number(form.probability_percent) : 0
        });
        onCreated?.({ type: 'opportunity', opportunity: response.data?.opportunity || null });
      }

      if (mode === 'task') {
        response = await axiosInstance.post('sales/activities/tasks/', {
          ...form,
          company_id: Number(form.company_id),
          opportunity_id: form.opportunity_id ? Number(form.opportunity_id) : undefined
        });
        onCreated?.({ type: 'task', task: response.data?.task || null });
      }

      onHide();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_SAVE_RECORD'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{t(getModalTitleKey(mode))}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          {mode === 'company' ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.COMPANY')}</Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={event => updateField('name', event.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.INDUSTRY')}</Form.Label>
                <Form.Control
                  value={form.industry}
                  onChange={event => updateField('industry', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.WEBSITE')}</Form.Label>
                <Form.Control
                  value={form.website}
                  onChange={event => updateField('website', event.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.SOURCE')}</Form.Label>
                <Form.Select value={form.source_type} onChange={event => updateField('source_type', event.target.value)}>
                  <option value="inbound">{t('SALES.SOURCE_INBOUND')}</option>
                  <option value="outbound">{t('SALES.SOURCE_OUTBOUND')}</option>
                  <option value="referral">{t('SALES.SOURCE_REFERRAL')}</option>
                  <option value="partner">{t('SALES.SOURCE_PARTNER')}</option>
                  <option value="organic">{t('SALES.SOURCE_ORGANIC')}</option>
                </Form.Select>
              </Form.Group>
            </>
          ) : null}

          {mode === 'contact' ? (
            <>
              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="mb-0">{t('SALES.COMPANY')}</Form.Label>
                  {onRequestModeChange ? (
                    <Button type="button" variant="link" size="sm" className="p-0" onClick={() => onRequestModeChange('company')}>
                      {t('SALES.NEW_COMPANY')}
                    </Button>
                  ) : null}
                </div>
                <Form.Select
                  value={form.company_id}
                  onChange={event => updateField('company_id', event.target.value)}
                  required
                >
                  <option value="">{t('SALES.SELECT_ONE')}</option>
                  {companyOptions.map(item => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
                {loadingCompanies ? <Form.Text>{t('SALES.LOADING_COMPANIES')}</Form.Text> : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.CONTACT_NAME')}</Form.Label>
                <Form.Control
                  value={form.full_name}
                  onChange={event => updateField('full_name', event.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.CONTACT_EMAIL')}</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={event => updateField('email', event.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.CONTACT_ROLE')}</Form.Label>
                <Form.Control
                  value={form.role}
                  onChange={event => updateField('role', event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>{t('SALES.PHONE')}</Form.Label>
                <Form.Control
                  value={form.phone}
                  onChange={event => updateField('phone', event.target.value)}
                />
              </Form.Group>
            </>
          ) : null}

          {mode === 'opportunity' ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.COMPANY')}</Form.Label>
                <Form.Select
                  value={form.company_id}
                  onChange={event => updateField('company_id', event.target.value)}
                  required
                >
                  <option value="">{t('SALES.SELECT_ONE')}</option>
                  {companyOptions.map(item => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
                {loadingCompanies ? <Form.Text>{t('SALES.LOADING_COMPANIES')}</Form.Text> : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.OPPORTUNITY')}</Form.Label>
                <Form.Control
                  value={form.title}
                  onChange={event => updateField('title', event.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.DESCRIPTION')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.description}
                  onChange={event => updateField('description', event.target.value)}
                />
              </Form.Group>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('SALES.PIPELINE_STAGE')}</Form.Label>
                    <Form.Select
                      value={form.pipeline_stage_id}
                      onChange={event => updateField('pipeline_stage_id', event.target.value)}
                    >
                      <option value="">{t('SALES.SELECT_ONE')}</option>
                      {stages.map(stage => (
                        <option key={stage.id} value={stage.id}>{stage.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('SALES.PRIORITY_OR_HEAT')}</Form.Label>
                    <Form.Select
                      value={form.interest_level}
                      onChange={event => updateField('interest_level', event.target.value)}
                    >
                      <option value="cold">{t('SALES.COLD')}</option>
                      <option value="warm">{t('SALES.WARM')}</option>
                      <option value="hot">{t('SALES.HOT')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('SALES.PROBABILITY')}</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      value={form.probability_percent}
                      onChange={event => updateField('probability_percent', event.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('SALES.EXPECTED_CLOSE')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.expected_close_date}
                      onChange={event => updateField('expected_close_date', event.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>{t('SALES.NEXT_FOLLOWUP')}</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={form.next_followup_at}
                      onChange={event => updateField('next_followup_at', event.target.value)}
                      required
                    />
                    <Form.Text>{t('SALES.NEXT_STEP_REQUIRED_HELP')}</Form.Text>
                  </Form.Group>
                </div>
              </div>
            </>
          ) : null}

          {mode === 'task' ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.COMPANY')}</Form.Label>
                <Form.Select
                  value={form.company_id}
                  onChange={event => updateField('company_id', event.target.value)}
                  required
                >
                  <option value="">{t('SALES.SELECT_ONE')}</option>
                  {companyOptions.map(item => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
                {loadingCompanies ? <Form.Text>{t('SALES.LOADING_COMPANIES')}</Form.Text> : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.OPPORTUNITY')}</Form.Label>
                <Form.Select
                  value={form.opportunity_id}
                  onChange={event => updateField('opportunity_id', event.target.value)}
                >
                  <option value="">{t('SALES.NO_OPPORTUNITY')}</option>
                  {companyOpportunities.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text>{t('SALES.TASK_OPPORTUNITY_HELP')}</Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.RELATED_CONTACTS')}</Form.Label>
                <div className="text-600 fs--1">
                  {loadingContacts ? <Spinner animation="border" size="sm" /> : t('SALES.RELATED_CONTACTS_HELP', { count: contacts.length })}
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.TASK')}</Form.Label>
                <Form.Control
                  value={form.title}
                  onChange={event => updateField('title', event.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('SALES.DESCRIPTION')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.description}
                  onChange={event => updateField('description', event.target.value)}
                />
              </Form.Group>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('SALES.DUE')}</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={form.due_at}
                      onChange={event => updateField('due_at', event.target.value)}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>{t('SALES.PRIORITY')}</Form.Label>
                    <Form.Select
                      value={form.priority}
                      onChange={event => updateField('priority', event.target.value)}
                    >
                      <option value="medium">{t('SALES.MEDIUM')}</option>
                      <option value="high">{t('SALES.HIGH')}</option>
                      <option value="urgent">{t('SALES.URGENT')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('SALES.CANCEL')}
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t('SALES.SAVING') : t('SALES.SAVE')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

SalesQuickCreateModal.propTypes = {
  show: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['company', 'contact', 'opportunity', 'task']).isRequired,
  onHide: PropTypes.func.isRequired,
  companies: PropTypes.array,
  opportunities: PropTypes.array,
  stages: PropTypes.array,
  defaultCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultOpportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCreated: PropTypes.func,
  onRequestModeChange: PropTypes.func
};

SalesQuickCreateModal.defaultProps = {
  companies: [],
  opportunities: [],
  stages: [],
  defaultCompanyId: '',
  defaultOpportunityId: '',
  onCreated: undefined,
  onRequestModeChange: undefined
};

export default SalesQuickCreateModal;
